import { z } from 'zod';
import CalendarDay from '../../../utils/calendar-day';
import { TrancheVisibility, FundStatus } from 'further-types/fund';
import { isMongoId } from '../../helpers';
import { validationRegexes } from '../../../utils/validation';

export const GetFundInput = z.object({
  id: z.string().refine(isMongoId()),
  firmId: z.string().refine(isMongoId()).optional(),
});

export const DeleteFundInput = z.object({
  id: z.string().refine(isMongoId()),
});

const FundFileBase = z.object({
  name: z.string(),
  path: z.string(),
  preview: z.string().optional(),
});

export const FundInput = z.object({
  firmId: z.string().refine(isMongoId()),
  fundName: z.string().min(1, 'Enter a fund name.'),
  fundStatus: z.nativeEnum(FundStatus),
  startDate: z.instanceof(CalendarDay).optional(),
  closedDate: z.instanceof(CalendarDay).optional(),
  finishDate: z.instanceof(CalendarDay, {
    message: 'Enter a scheduled close date.',
  }),
  tag: z.string().refine(isMongoId()),
  isProfessionalClientsOnly: z.boolean().optional(),
  trancheVisibility: z.nativeEnum(TrancheVisibility).optional(),
  fundRaiseLimit: z.number().optional(),
  incomeTaxRebate: z.string().optional(),
  targetReturn: z.string().optional(),
  targetPortfolioSize: z.string().optional(),
  minimumDirectInvestment: z.string().optional(),
  minimumAdvisedInvestment: z.string().optional(),
  minimumIncludesAdviceFee: z.boolean().optional(),
  applicationFormFile: FundFileBase.nullable().optional(),
  prospectusFile: FundFileBase.nullable().optional(),
  keyInfoFile: FundFileBase.nullable().optional(),
  additionalDocuments: z.array(FundFileBase).optional(),
  defaultCoreFee: z.string().optional(),
  defaultPerformanceFee: z.string().optional(),
  firmBankDetailId: z.string().optional(),
  title: z.string().optional(),
  customBankTransferReference: z.string().optional(),
  whatToExpectNow: z.string().optional(),
  additionalAttachment: FundFileBase.nullable().optional(),
  additionalInfo: z
    .string()
    .optional()
    .refine(
      (value) =>
        !value ||
        value
          .split(';')
          .map((email) => email.trim())
          .every((email) => !email || validationRegexes.email.test(email)),
      {
        message:
          'Additional info must be a semicolon-separated list of valid email addresses.',
      },
    ),
});

export const CreateFundInput = FundInput.extend({
  fundStatus: z.union([
    z.literal(FundStatus.Draft),
    z.literal(FundStatus.Closed),
  ]),
});

export const UpdateFundInput = FundInput.extend({
  id: z.string().refine(isMongoId()),
});

export const UpdateFundStatusInput = z.object({
  id: z.string().refine(isMongoId()),
  firmId: z.string().refine(isMongoId()).optional(),
  fundStatus: z.nativeEnum(FundStatus),
});

export const ExportPerformanceFeeSimulationInput = z.object({
  firmId: z.string().optional(),
  fundName: z.string().optional(),
  tagId: z.string().optional(),
  snapshotDate: z.instanceof(CalendarDay).optional(),
  fundStatus: z.array(z.string()).optional(),
  fullyDeployed: z.boolean().optional(),
  startDate: z.instanceof(CalendarDay).optional(),
  endDate: z.instanceof(CalendarDay).optional(),
  fundId: z.string().optional(),
});
